import { Link, router } from '@inertiajs/react'
import { UserSidebar } from '@/components/layout/UserSidebar'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'

interface Props {
  subscription_status: string
  subscription: {
    plan: string; status: string
    current_period_end: string | null; cancel_at_period_end: boolean
  } | null
}

function csrfToken() {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
}

export default function Index({ subscription_status, subscription }: Props) {
  const isPro = subscription_status === 'pro'

  const startCheckout = (plan: 'monthly' | 'yearly') => {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `/subscriptions/checkout?plan=${plan}`
    const csrf = document.createElement('input')
    csrf.type = 'hidden'
    csrf.name = '_token'
    csrf.value = csrfToken()
    form.appendChild(csrf)
    document.body.appendChild(form)
    form.submit()
  }

  const openPortal = () => {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/subscriptions/portal'
    const csrf = document.createElement('input')
    csrf.type = 'hidden'
    csrf.name = '_token'
    csrf.value = csrfToken()
    form.appendChild(csrf)
    document.body.appendChild(form)
    form.submit()
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <UserSidebar active="/billing" />
      <main className="flex-1 p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Billing</h1>

        <div className="max-w-lg space-y-5">
          {/* Current plan */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Current plan</h2>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-gray-900 text-lg">
                  {isPro ? 'Pro Monthly' : 'Free'}
                </p>
                {isPro && subscription?.current_period_end && (
                  <p className="text-sm text-gray-400 mt-0.5">
                    {subscription.cancel_at_period_end ? 'Cancels' : 'Renews'} on{' '}
                    {new Date(subscription.current_period_end).toLocaleDateString()}
                  </p>
                )}
                {!isPro && (
                  <p className="text-sm text-gray-400 mt-0.5">Access to free episodes only</p>
                )}
              </div>
              <span className={cn(
                'text-xs font-medium px-3 py-1 rounded-full',
                isPro ? 'bg-brand/10 text-brand' : 'bg-gray-100 text-gray-500'
              )}>
                {isPro ? 'Active' : 'Free'}
              </span>
            </div>
          </div>

          {/* Upgrade / manage */}
          {!isPro ? (
            <div className="bg-brand/5 border border-brand/20 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Unlock all Pro episodes</h3>
              <p className="text-sm text-gray-500 mb-4">
                Get full access to every episode, progress tracking, and bookmarks.
              </p>
              <div className="flex gap-3">
                <Button onClick={() => startCheckout('monthly')}>Start Pro Monthly — $9/mo</Button>
                <Button variant="secondary" onClick={() => startCheckout('yearly')}>Pro Yearly — $79/yr</Button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Manage subscription</h2>
              <div className="space-y-3 text-sm">
                <button
                  onClick={openPortal}
                  className="w-full text-left px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Switch to yearly plan <span className="text-gray-400 text-xs">(save $29/year)</span>
                </button>
                <button
                  onClick={openPortal}
                  className="w-full text-left px-4 py-3 border border-red-100 text-red-500 rounded-xl hover:bg-red-50 transition-colors"
                >
                  Cancel subscription
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-4">
                You'll keep Pro access until the end of your current billing period.
              </p>
            </div>
          )}

          {/* Invoice note */}
          <p className="text-xs text-gray-400 text-center">
            Payments are processed securely by Stripe. For invoice history, contact support.
          </p>
        </div>
      </main>
    </div>
  )
}
